/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect, useState } from "react";
import { Alert, Box, Paper, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import LayoutPublicRoutes from "@/src/containers/Layouts/LayoutPublicRoutes";
import Container from "@/src/components/Container/Container";
import LandingPageTeaser from "@/src/components/Teaser/LandingPageTeaser";
import LinkedCardBox from "@/src/components/LinkedCardBox/LinkedCardBox";
import { defaultProvenExpertTeaserCard } from "@/src/services/landingPageService";
import iconAttorneys from "@/src/assets/icons/generic/IconLawyers";
import iconDocument from "@/src/assets/icons/generic/IconDocument";
import PartnerLogoSection from "@/src/components/Sections/Generic/PartnerLogoSection/PartnerLogoSection";
import HowItWorksSectionConsulting from "@/src/components/Sections/FamilyLaw/Consulting/HowItWorksSection/HowItWorksSectionConsulting";
import AttorneySection from "@/src/components/Sections/Generic/AttorneySection/AttorneySection";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import EmailCheck from "@/src/components/EmailCheck/EmailCheck";
import ButtonLoading from "@/src/components/Button/ButtonLoading";
import ButtonTextTermsOfServicePrivacy from "@/src/components/Checkin/Common/ButtonTextTermsOfServicePrivacy/ButtonTextTermsOfServicePrivacy";
import basePersonConsultingCheckinStyle from "@/src/components/Checkin/Common/Consulting/basePersonConsultingCheckinStyle";
import { FormProvider, useForm } from "@/src/provider/Form/FormProvider";
import { emailDefault, requiredValidator } from "@/src/services/validationRules";
import { completeSmsRegistration } from "@/src/services/customerService";
import type { SmsRegistrationContext } from "@/src/types/SmsRegistrationContext";
import { fetchResource, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import { logout } from "@/src/store/login/actions";
import SubmissionError from "@/src/error/SubmissionError";

function SmsRegistrationForm({
  isLoading,
  emailErrorText,
  setEmailErrorText,
}: {
  isLoading: boolean;
  emailErrorText: string;
  setEmailErrorText: (text: string) => void;
}) {
  const { handleSubmit } = useForm();

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <EmailCheck
        name="email"
        label="E-Mail-Adresse"
        autoComplete="email"
        disabled={isLoading}
        emailErrorText={emailErrorText}
        setEmailErrorText={setEmailErrorText}
        validators={[requiredValidator("Bitte geben Sie Ihre E-Mail-Adresse an"), emailDefault]}
      />
      <ButtonLoading variant="contained" color="primary" type="submit" fullWidth isLoading={isLoading}>
        Weiter
      </ButtonLoading>
      <ButtonTextTermsOfServicePrivacy />
    </Box>
  );
}

export default function SmsRegistrationPage() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id") ?? "";
  const token = searchParams.get("t") ?? "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [expiredLinkError, setExpiredLinkError] = useState("");

  // Match the email registration page: the link identifies the customer for this session.
  useEffect(() => {
    logout(queryClient);
  }, [id, token, queryClient]);

  const { data, error, isLoading } = useQuery<SmsRegistrationContext>({
    queryKey: ["registrationSms", id, token],
    queryFn: () => fetchResource(
      `/registration_sms?${new URLSearchParams({ id, t: token })}`,
      undefined,
      { headers: new Headers({ Authorization: "" }) },
    ),
    retry: false,
  });

  const loadingError = error instanceof SubmissionError
    ? error.errors.find((violation) => violation.field === "token")?.message
    : null;

  const handleSubmit = async ({ values }: { values: { email: string } }) => {
    setIsSubmitting(true);
    setEmailErrorText("");
    setSubmitError("");

    try {
      const registration = await completeSmsRegistration(id, token, values.email);
      localStorage.setItem("token", registration.token);
      await queryClient.invalidateQueries({ queryKey: queryKeys.collection("/customers") });
      navigate(registration.target, { replace: true });
    } catch (error) {
      if (error instanceof SubmissionError) {
        const tokenError = error.errors.find((violation) => violation.field === "token");
        if (tokenError) {
          setExpiredLinkError(tokenError.message);
        } else {
          const emailError = error.errors.find((violation) => violation.field === "email");
          if (emailError) {
            setEmailErrorText(emailError.message);
          } else {
            setSubmitError(error.errors.find((violation) => violation.field === "_error")?.message ?? error.message);
          }
        }
      } else {
        setSubmitError("Leider ist ein technischer Fehler aufgetreten. Bitte versuchen Sie es später erneut.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LayoutPublicRoutes minimal clickableLogo={false}>
      <Helmet>
        <title>Registrierung vervollständigen | Legalbird</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="referrer" content="no-referrer" />
      </Helmet>
      <LandingPageTeaser
        headline="Professionelle Hilfe bei Ihrem Anliegen"
        subheadline="Vervollständigen Sie Ihre Daten, damit wir Sie bei Ihrem Anliegen unterstützen können."
        hasTeaserButton={false}
      >
        <LinkedCardBox
          linkedCards={[
            defaultProvenExpertTeaserCard,
            { icon: iconAttorneys, heading: "Professionelle Beratung" },
            { icon: iconDocument, heading: "Unkomplizierte Abwicklung" },
          ]}
        />
      </LandingPageTeaser>
      {!error && !expiredLinkError && data?.partner && <PartnerLogoSection partnerIri={data.partner} containerStyle="settlementContainer" />}
      <Container variant="sm">
        {isLoading && <LegalbirdLoader />}
        {(error || expiredLinkError) && (
          <Alert severity="error">
            {expiredLinkError || loadingError || "Leider ist ein technischer Fehler aufgetreten. Bitte versuchen Sie es später erneut."}
          </Alert>
        )}
        {data && !error && !expiredLinkError && (
          <Paper elevation={4} sx={basePersonConsultingCheckinStyle.formPaper}>
            <Typography variant="h5" component="h2" sx={basePersonConsultingCheckinStyle.heading}>
              Jetzt Daten vervollständigen
            </Typography>
            <Typography sx={{ marginTop: 2 }}>{data.salutation},</Typography>
            <Typography sx={{ marginTop: 1 }}>
              Bitte geben Sie Ihre E-Mail-Adresse an. Wir senden Ihnen eine E-Mail zur Bestätigung. Anschließend können Sie Ihren Fall direkt vervollständigen.
            </Typography>
            {submitError && <Alert severity="error" sx={{ marginTop: 2 }}>{submitError}</Alert>}
            <FormProvider initialValues={{ email: "" }} submitAction={handleSubmit}>
              <SmsRegistrationForm isLoading={isSubmitting} emailErrorText={emailErrorText} setEmailErrorText={setEmailErrorText} />
            </FormProvider>
          </Paper>
        )}
      </Container>
      <HowItWorksSectionConsulting />
      <AttorneySection
        subheading="Sie möchten sich professionell und schnell unterstützen lassen? Wir helfen Ihnen - jederzeit und ortsunabhängig!"
        heading="Deutschlandweit tätige Partneranwälte"
      />
    </LayoutPublicRoutes>
  );
}
